'use client';

import React, { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';
import { RichTextContent } from '@/components/RichTextEditor';
import { useTranslation } from 'react-i18next';

type Category = Database['public']['Tables']['categories']['Row'];

// Add an interface to define the producer type
interface Producer {
  id: string;
  company_name: string;
  logo_url?: string;
  description?: string;
  user_id?: string;
  categories?: any[];
  profiles?: {
    first_name?: string;
    last_name?: string;
  } | null;
  uniqueCategories?: string[];
}

export function Home() {
  const [loading, setLoading] = useState(true);
  const [producers, setProducers] = useState<Producer[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [openProducerId, setOpenProducerId] = useState<string | null>(null);
  const [categoryError, setCategoryError] = React.useState<string | null>(null);
  const [categoryLoading, setCategoryLoading] = React.useState(true);
  const [loginSuccess, setLoginSuccess] = React.useState(false);
  const { t } = useTranslation();

  React.useEffect(() => {
    // Check for login success message
    const hasLoginSuccess = localStorage.getItem('loginSuccess') === 'true';
    if (hasLoginSuccess) {
      setLoginSuccess(true);
      localStorage.removeItem('loginSuccess');
      // Clear the success message after 3 seconds
      const timer = setTimeout(() => {
        setLoginSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  React.useEffect(() => {
    // Fetch categories
    async function fetchCategories() {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (err) {
        logger.error('Error fetching categories:', err);
        setCategoryError('Failed to load categories');
      } finally {
        setCategoryLoading(false);
      }
    }

    fetchCategories();

    async function fetchProducers() {
      const { data, error } = await supabase
        .from('producers')
        .select(`
          *,
          categories:products(categories(name))
        `)
        .eq('is_approved', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) {
        logger.error('Error fetching producers:', error);
        return;
      }

      if (data && data.length > 0) {
        // Get all user IDs from producers
        const userIds = data.map(producer => producer.user_id).filter(Boolean);
        
        // Fetch profiles if there are user IDs
        let profilesMap: { [key: string]: any } = {};
        if (userIds.length > 0) {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', userIds);
            
          // Create a map of profiles by ID
          profilesMap = (profilesData || []).reduce((acc: { [key: string]: any }, profile) => {
            acc[profile.id] = profile;
            return acc;
          }, {});
        }
        
        // Process categories to remove duplicates and add profiles
        const processedProducers = data.map(producer => ({
          ...producer,
          profiles: profilesMap[producer.user_id] || null,
          uniqueCategories: [...new Set(
            (producer.categories || [])
              .flatMap((product: any) => product.categories)
              .filter(Boolean)
              .map((category: any) => category.name)
          )]
        }));

        setProducers(processedProducers);
        setLoading(false);
      }
    }

    fetchProducers();
  }, []);

  return (
    <div className="bg-white">
      {/* Semesterstängt banner */}
      <div className="bg-red-600 text-white p-4 text-center">
        <h2 className="text-2xl font-bold">🏖️ Semesterstängt för sommaren 🏖️</h2>
        <p>Vi är tillbaka efter sommaren med en förbättrad sida, fler produkter och bättre upplevelse för kunden. Välkommen</p>
      </div>
      
      {/* Success message after login */}
      {loginSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
          <strong className="font-bold">{t('common.welcome')}</strong>
          <span className="absolute top-0 bottom-0 right-0 px-4 py-3">
            <svg onClick={() => setLoginSuccess(false)} className="fill-current h-6 w-6 text-green-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><title>{t('common.close')}</title><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/></svg>
          </span>
        </div>
      )}

      {/* Hero section */}
      <div className="relative">
        <div className="absolute inset-0">
          <img
            className="h-full w-full object-cover"
            src="https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80"
            alt="Fresh vegetables"
          />
          <div className="absolute inset-0 bg-gray-900 bg-opacity-50" />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t('home.hero.title')}
          </h1>
          <p className="mt-6 text-xl text-white max-w-3xl">
            {t('home.hero.description')}
          </p>
          <div className="mt-10">
            <Link
              to="/products"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              {t('home.hero.cta')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Featured Producers */}
      <div className="bg-gray-50">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <h2 className="text-3xl font-extrabold text-gray-900">
              {t('home.producers.title')}
            </h2>
            <Link
              to="/producers"
              className="text-green-600 hover:text-green-700 font-medium flex items-center"
            >
              {t('home.producers.viewAll')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
          
          {loading ? (
            <div className="mt-12 grid grid-cols-1 gap-y-12 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-64 bg-gray-200 rounded-lg" />
                  <div className="mt-4 h-4 bg-gray-200 rounded w-3/4" />
                  <div className="mt-2 h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 gap-y-12 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-3">
              {producers.map((producer) => (
                <Link
                  key={producer.id}
                  to={`/producers/${producer.id}`}
                  className="group"
                >
                  <div className="relative h-64 w-full overflow-hidden rounded-lg bg-white">
                    {producer.logo_url ? (
                      <img
                        src={producer.logo_url}
                        alt={producer.company_name}
                        className="h-full w-full object-contain bg-gray-50"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gray-100">
                        <span className="text-gray-400 text-2xl font-medium">
                          {producer.company_name.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {producer.company_name}
                    </h3>
                    {producer.description && (
                      <div className="mt-1 text-sm text-gray-500 line-clamp-2">
                        <RichTextContent 
                          content={
                            producer.description.length > 100
                              ? `${producer.description.slice(0, 100)}...`
                              : producer.description
                          } 
                        />
                      </div>
                    )}
                    {producer.uniqueCategories && producer.uniqueCategories.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {producer.uniqueCategories.map((category: string, index: number) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                          >
                            {category}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
          {!loading && producers.length === 0 && (
            <div className="mt-8 text-center text-gray-500">
              No producers found
            </div>
          )}
          <div className="mt-12 text-center">
            <Link
              to="/producers"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              Se alla producenter
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}